import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { tc, ts } from 'utils/theme';

import Label from 'components/Atoms/Label';
import FieldError from 'components/Atoms/FieldError';
import FieldHelp from 'components/Atoms/FieldHelp';

const StyledDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledInput = styled.input`
  border: ${(p) =>
    p.error
      ? `1px solid ${tc('errorMessage')(p)}`
      : `1px solid ${tc('boxBorderColor')(p)}`};
  border-top-left-radius: ${(p) => (p.prefix ? '0px' : ts('borderRadius')(p))};
  border-top-right-radius: ${ts('borderRadius')};
  border-bottom-left-radius: ${(p) =>
    p.prefix ? '0px' : ts('borderRadius')(p)};
  border-bottom-right-radius: ${ts('borderRadius')};
  color: ${(p) => (p.error ? tc('errorMessage')(p) : tc('labelColor')(p))};
  font-size: 14px;
  height: 40px;
  line-height: 40px;
  margin-top: 5px;
  padding: 0 15px;
  width: ${(p) => (p.prefix ? 'calc(100% - 34px)' : '100%')};
`;

const StyledPrefix = styled.div`
  background: #f3f3f3;
  border-color: ${(p) =>
    p.error ? tc('errorMessage')(p) : tc('borderColor')(p)};
  border-top-left-radius: ${ts('borderRadius')};
  border-bottom-left-radius: ${ts('borderRadius')};
  border-style: solid;
  border-width: 1px 0 1px 1px;
  color: ${tc('iconColor')};
  height: ${ts('formHeight')};
  line-height: ${ts('formHeight')};
  text-align: center;
  margin-top: 5px;
  width: 34px;
`;

/**
 * Text input form element
 * @param {string} id
 * @param {string} name name of the input
 * @param {string} label display text of the element label
 * @param {string} prefix display prefix block on element
 * @param {string} placeholder display placeholder in input
 * @param {string} type type of input
 * @param {string} error error to show
 * @param {string} help help message to show
 * @param {boolean} disabled boolean to disable or enable input
 * @param {boolean} required boolean to show whether the element is required
 * @param {function} onChange triggered when the value changes
 * @param {string} maxLength set input's max character length
 * @param {string} defaultValue set default for uncontrolled inputs
 * @param {string} value provided for controlled inputs
 * @param {boolean} controlled make this a controlled input
 */
const TextInput = ({
  id,
  autoComplete,
  name,
  label,
  prefix,
  placeholder,
  type,
  error,
  help,
  disabled,
  required,
  onChange,
  maxLength,
  defaultValue,
  value,
  controlled,
  readOnly,
  min,
  ...props
}) => (
  <div {...props}>
    <Label htmlFor={id || name} required={required} error={error}>
      {label}
    </Label>
    <StyledDiv>
      {prefix && <StyledPrefix error={error}>{prefix}</StyledPrefix>}
      <StyledInput
        id={id || name}
        name={name}
        type={type}
        placeholder={placeholder}
        autoComplete={autoComplete}
        error={error}
        prefix={prefix}
        disabled={disabled ? 'disabled' : ''}
        onChange={onChange}
        maxLength={maxLength}
        readOnly={readOnly}
        {...(defaultValue && { defaultValue })} // Uncontrolled
        {...(controlled && { value })} // Controlled
        min={min}
      />
    </StyledDiv>
    <FieldHelp help={help} />
    <FieldError error={error} />
  </div>
);

TextInput.propTypes = {
  id: PropTypes.string,
  autoComplete: PropTypes.oneOf(['on', 'off']),
  name: PropTypes.string.isRequired,
  prefix: PropTypes.string,
  type: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  help: PropTypes.string,
  defaultValue: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  onChange: PropTypes.func,
  maxLength: PropTypes.number,
  controlled: PropTypes.bool,
  readOnly: PropTypes.bool,
  min: PropTypes.string,
};

TextInput.defaultProps = {
  id: '',
  autoComplete: 'off',
  type: 'text',
  prefix: '',
  label: '',
  placeholder: '',
  error: '',
  help: '',
  defaultValue: undefined,
  value: undefined,
  disabled: false,
  required: false,
  onChange: () => {},
  maxLength: 255,
  controlled: false,
  readOnly: false,
};

export default TextInput;
